import { useCallback, useEffect, useRef } from 'react'

/*
 * Ein Fenster schliessen, ohne den Schliessknopf zu treffen: Klick auf die
 * Flaeche daneben, oder Esc.
 *
 * Zwei Feinheiten, die den Unterschied zwischen "geht" und "nervt" ausmachen:
 *
 * - Der Klick zaehlt nur, wenn er auf dem Hintergrund *beginnt und endet*.
 *   Mit einem einfachen onClick auf dem Hintergrund schliesst sich das Fenster
 *   auch dann, wenn man darin Text markiert und die Maus dabei ueber den Rand
 *   hinaus loslaesst - der Klick "endet" dann auf dem Hintergrund.
 * - Bei mehreren offenen Fenstern reagiert nur das oberste auf Esc. Dafuer
 *   fuehren wir einen Stapel; wer zuletzt geoeffnet hat, wird zuerst gefragt.
 */

const stack = []

/**
 * Ist gerade ein Fenster offen?
 *
 * Seiten, die Esc selbst belegen (Firmenprofil und Unternehmensprofil: Esc
 * geht zurueck), fragen hier nach. Ohne das schloss Esc in einem offenen
 * Fenster nicht nur dieses, sondern verliess nebenbei die ganze Seite — beide
 * Horcher hoerten denselben Tastendruck.
 */
export const isDialogOpen = () => stack.length > 0

export default function useDialogDismiss(onDismiss, { enabled = true } = {}) {
	// Der Aufrufer gibt bei jedem Rendern eine neue Funktion herein. Ueber die
	// Referenz haengt der Tastatur-Horcher nicht daran und wird nicht bei jedem
	// Tastendruck neu angemeldet.
	const dismissRef = useRef(onDismiss)
	dismissRef.current = onDismiss

	const startedOnBackdrop = useRef(false)

	useEffect(() => {
		if (!enabled) return undefined

		const entry = () => dismissRef.current()
		stack.push(entry)

		const handleKeyDown = event => {
			if (event.key !== 'Escape') return
			// Hat schon jemand anderes auf Esc reagiert (ein offenes Auswahlmenue,
			// ein Kalender), bleibt das Fenster stehen.
			if (event.defaultPrevented) return
			if (stack[stack.length - 1] !== entry) return
			entry()
		}

		document.addEventListener('keydown', handleKeyDown)

		return () => {
			document.removeEventListener('keydown', handleKeyDown)
			const index = stack.indexOf(entry)
			if (index !== -1) stack.splice(index, 1)
		}
	}, [enabled])

	const onMouseDown = useCallback(event => {
		startedOnBackdrop.current = event.target === event.currentTarget
	}, [])

	const onMouseUp = useCallback(
		event => {
			const onBackdrop = startedOnBackdrop.current && event.target === event.currentTarget
			startedOnBackdrop.current = false
			if (onBackdrop && enabled) dismissRef.current()
		},
		[enabled]
	)

	return { onMouseDown, onMouseUp }
}
